/**
 * FerramentasPage — Tabela SUS e uso de tokens (F11).
 *
 * Duas telas que não pertencem a domínio de negócio nenhum:
 *  - a Tabela SUS/SIGTAP, referência de código, descrição e valor de cada
 *    procedimento — é dela que o parecer do SUS tira o que cita;
 *  - o consumo do Gemini por período, função, dia e tipo de requisição.
 */

import {
  faArrowsRotate,
  faChartLine,
  faCircleCheck,
  faCloudUploadAlt,
  faCoins,
  faExclamationTriangle,
  faHospitalAlt,
  faSpinner,
  faTableList,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  coletarTabelaSUS,
  enviarPlanilhaTabelaSUS,
  estatisticasTabelaSUS,
  usoDeTokens,
} from '../../api/ferramentas'
import { useToast } from '../../components/Toast'

const CAMPO =
  'rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

const numero = (n: number) => n.toLocaleString('pt-BR')

function Cabecalho({
  icone,
  titulo,
  subtitulo,
}: {
  icone: typeof faTableList
  titulo: string
  subtitulo: string
}) {
  return (
    <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <div className="bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-900 p-6 text-white">
        <h1 className="flex items-center gap-3 text-2xl font-bold">
          <FontAwesomeIcon icon={icone} />
          {titulo}
        </h1>
        <p className="mt-1 text-purple-200">{subtitulo}</p>
      </div>
    </header>
  )
}

// ── Tabela SUS ──────────────────────────────────────────────────────────────

export function TabelaSusPage() {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const entrada = useRef<HTMLInputElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['ferramentas', 'tabela-sus'],
    queryFn: estatisticasTabelaSUS,
  })

  const enviar = useMutation({
    mutationFn: (arquivo: File) => enviarPlanilhaTabelaSUS(arquivo),
    onSuccess: (r) => {
      notificar(
        'success',
        `${r.atualizados} procedimento(s) atualizados e ${r.inseridos} novo(s)` +
          `${r.falhas ? ` · ${r.falhas} linha(s) recusadas` : ''}.`,
      )
      void clienteQuery.invalidateQueries({ queryKey: ['ferramentas'] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const coletar = useMutation({
    mutationFn: coletarTabelaSUS,
    onSuccess: () =>
      notificar(
        'success',
        'Coleta do DATASUS iniciada — leva alguns minutos e roda em segundo plano.',
      ),
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div className="mx-auto max-w-5xl">
      <Cabecalho
        icone={faHospitalAlt}
        titulo="Tabela SUS"
        subtitulo="Procedimentos do SIGTAP: código, descrição, financiamento e valores."
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ['Procedimentos', isLoading ? '…' : numero(data?.total ?? 0)],
          ['Atualizada em', data?.atualizada_em || '—'],
          ['Tipos de financiamento', String(data?.financiamentos ?? 0)],
        ].map(([rotulo, valor]) => (
          <div
            key={rotulo}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
          >
            <p className="text-xs uppercase text-slate-500">{rotulo}</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {valor}
            </p>
          </div>
        ))}
      </section>

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faCloudUploadAlt} className="text-purple-600" />
          Atualizar pela planilha
        </h2>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Planilha do DATASUS com as colunas: código, descrição,
          financiamento, valor ambulatorial e valor de serviço hospitalar. O
          que já existe é atualizado pelo código; o resto é inserido.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            disabled={enviar.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={enviar.isPending ? faSpinner : faCloudUploadAlt}
              spin={enviar.isPending}
            />
            {enviar.isPending ? 'Processando…' : 'Escolher planilha'}
          </button>
          <input
            ref={entrada}
            id="planilha"
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const arquivo = e.target.files?.[0]
              if (arquivo) enviar.mutate(arquivo)
              e.target.value = ''
            }}
          />
          <button
            type="button"
            onClick={() => {
              if (confirm('Iniciar a coleta automática do DATASUS?'))
                coletar.mutate()
            }}
            disabled={coletar.isPending}
            className="inline-flex items-center gap-2 rounded-xl border border-purple-500 px-6 py-3 font-medium text-purple-700 hover:bg-purple-50 disabled:opacity-50 dark:text-purple-300"
          >
            <FontAwesomeIcon
              icon={coletar.isPending ? faSpinner : faArrowsRotate}
              spin={coletar.isPending}
            />
            Atualizar do DATASUS
          </button>
        </div>

        {data?.ultima_coleta && (
          // A coleta roda em segundo plano: sem este aviso, a falha ficava
          // só no log do worker e a tela seguia dizendo "iniciada".
          <p
            className={`mt-4 rounded-lg border px-4 py-2 text-sm ${
              data.ultima_coleta.sucesso
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
                : 'border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
            }`}
          >
            <FontAwesomeIcon
              icon={data.ultima_coleta.sucesso ? faCircleCheck : faTriangleExclamation}
              className="mr-2"
            />
            Última coleta do DATASUS em {data.ultima_coleta.em}:{' '}
            {data.ultima_coleta.sucesso ? 'concluída' : 'FALHOU'} —{' '}
            {data.ultima_coleta.mensagem}
            {data.ultima_coleta.total != null &&
              ` (${numero(data.ultima_coleta.total)} procedimentos)`}
          </p>
        )}
      </section>

      {(data?.por_financiamento ?? []).length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
            Procedimentos por financiamento
          </h2>
          <ul className="space-y-2">
            {(data?.por_financiamento ?? []).map((f) => (
              <li
                key={f.nome}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-slate-700"
              >
                <span className="text-slate-700 dark:text-slate-300">
                  {f.nome}
                </span>
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  {numero(f.total)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

// ── Uso de tokens ───────────────────────────────────────────────────────────

function hoje() {
  return new Date().toISOString().slice(0, 10)
}

export function TokensPage() {
  const [inicio, setInicio] = useState(hoje())
  const [fim, setFim] = useState(hoje())
  const [filtro, setFiltro] = useState({ inicio: hoje(), fim: hoje() })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ferramentas', 'tokens', filtro],
    queryFn: () => usoDeTokens(filtro.inicio, filtro.fim),
  })

  const dolar = (valor: number) =>
    `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`

  return (
    <div className="mx-auto max-w-6xl">
      <Cabecalho
        icone={faChartLine}
        titulo="Uso de tokens"
        subtitulo="Consumo do Gemini por período, com o custo estimado pela tabela do modelo."
      />

      <section className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          De
          <input
            id="inicio"
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            className={`mt-1 block ${CAMPO}`}
          />
        </label>
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Até
          <input
            id="fim"
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            className={`mt-1 block ${CAMPO}`}
          />
        </label>
        <button
          type="button"
          onClick={() => setFiltro({ inicio, fim })}
          className="rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-2.5 font-semibold text-white shadow-lg"
        >
          Aplicar
        </button>
        {data && (
          <span className="text-xs text-slate-500">
            Preços aplicados: US$ {data.preco_entrada}/1M na entrada e US${' '}
            {data.preco_saida}/1M na saída.
          </span>
        )}
      </section>

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          Não foi possível carregar o consumo.
        </div>
      )}

      {isLoading || !data ? (
        <p className="p-8 text-center text-slate-500">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
          Carregando…
        </p>
      ) : (
        <>
          <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Requisições', numero(data.requisicoes), ''],
              [
                'Tokens',
                numero(data.tokens_total),
                `${numero(data.tokens_entrada)} entrada · ${numero(data.tokens_saida)} saída`,
              ],
              [
                'Média por requisição',
                numero(data.media_por_requisicao),
                'tokens',
              ],
              [
                'Custo estimado',
                dolar(data.custo_total),
                `${dolar(data.custo_entrada)} + ${dolar(data.custo_saida)}`,
              ],
            ].map(([rotulo, valor, detalhe]) => (
              <div
                key={rotulo}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
              >
                <p className="text-xs uppercase text-slate-500">{rotulo}</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {valor}
                </p>
                {detalhe && (
                  <p className="text-xs text-slate-500">{detalhe}</p>
                )}
              </div>
            ))}
          </section>

          {data.requisicoes === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
              Nenhuma geração no período.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {(
                [
                  ['Por função', data.por_funcao],
                  ['Por tipo de requisição', data.por_tipo],
                ] as const
              ).map(([titulo, linhas]) => (
                <section
                  key={titulo}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
                >
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
                    <FontAwesomeIcon icon={faCoins} className="text-purple-600" />
                    {titulo}
                  </h2>
                  {linhas.length === 0 ? (
                    <p className="text-sm text-slate-500">Sem dados.</p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                          <th className="py-2 pr-4">Nome</th>
                          <th className="py-2 pr-4">Requisições</th>
                          <th className="py-2 pr-4">Tokens</th>
                          <th className="py-2">Média</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((l) => (
                          <tr
                            key={l.nome}
                            className="border-b border-slate-100 dark:border-slate-700"
                          >
                            <td className="py-2 pr-4 text-slate-700 dark:text-slate-300">
                              {l.nome}
                            </td>
                            <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                              {numero(l.requisicoes)}
                            </td>
                            <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                              {numero(l.tokens)}
                            </td>
                            <td className="py-2 text-slate-600 dark:text-slate-400">
                              {numero(l.media)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              ))}

              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card lg:col-span-2">
                <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Por dia
                </h2>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                      <th className="py-2 pr-4">Dia</th>
                      <th className="py-2 pr-4">Requisições</th>
                      <th className="py-2">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.por_dia.map((d) => (
                      <tr
                        key={d.dia}
                        className="border-b border-slate-100 dark:border-slate-700"
                      >
                        <td className="py-2 pr-4 text-slate-700 dark:text-slate-300">
                          {d.dia}
                        </td>
                        <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                          {numero(d.requisicoes)}
                        </td>
                        <td className="py-2 text-slate-600 dark:text-slate-400">
                          {numero(d.tokens)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  )
}
